/**
 * The title's background: a faint field of stars over pure black.
 *
 * A standalone 2D canvas (not the live 3D scene) so the title paints instantly
 * and stays art-directed — but the per-star tint mirrors the in-game starfield
 * (`makeStarfield` in world/starsystem.ts): a warm↔cool bias with a low base
 * brightness, kept deliberately dim here so the white line motif owns the frame.
 */

interface Star {
  x: number; // 0..1 of width
  y: number; // 0..1 of height
  r: number; // radius px
  a: number; // base alpha
  tw: number; // twinkle speed
  ph: number; // twinkle phase
  cr: number;
  cg: number;
  cb: number;
}

/** Mulberry32 — a tiny deterministic PRNG so the field is stable across loads. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class IntroStars {
  readonly el: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private stars: Star[] = [];
  private raf = 0;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private t0 = 0;
  private ro: ResizeObserver | null = null;

  constructor(density = 0.00018) {
    this.el = document.createElement("canvas");
    this.el.className = "intro-stars";
    this.ctx = this.el.getContext("2d")!;
    this.density = density;
    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this.el);
    }
  }

  private density: number;

  private resize(): void {
    const r = this.el.getBoundingClientRect();
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.el.width = Math.round(this.w * this.dpr);
    this.el.height = Math.round(this.h * this.dpr);
    this.seed();
  }

  private seed(): void {
    const count = Math.round(this.w * this.h * this.density);
    const rnd = rng(0x9e3779b1);
    const stars: Star[] = [];
    for (let i = 0; i < count; i++) {
      const warm = rnd();
      // Base brightness kept low; a rare few are allowed to be a touch brighter.
      const bright = 0.28 + rnd() * 0.42 + (rnd() > 0.94 ? 0.25 : 0);
      stars.push({
        x: rnd(),
        y: rnd(),
        r: 0.5 + rnd() * rnd() * 1.4,
        a: 0.22 + rnd() * 0.5,
        tw: 0.3 + rnd() * 1.1,
        ph: rnd() * Math.PI * 2,
        cr: bright * (0.8 + warm * 0.2),
        cg: bright * 0.9,
        cb: bright * (1.0 - warm * 0.15),
      });
    }
    this.stars = stars;
  }

  start(): void {
    if (this.raf) return;
    if (!this.w) this.resize();
    this.t0 = performance.now();
    const tick = (now: number): void => {
      this.draw((now - this.t0) / 1000);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  dispose(): void {
    this.stop();
    this.ro?.disconnect();
    this.el.remove();
  }

  private draw(t: number): void {
    const { ctx, dpr } = this;
    const W = this.w * dpr;
    const H = this.h * dpr;
    ctx.clearRect(0, 0, W, H);
    // A very slow drift gives the field a hint of parallax life.
    const driftX = Math.sin(t * 0.02) * 6 * dpr;
    const driftY = Math.cos(t * 0.015) * 4 * dpr;
    for (const s of this.stars) {
      const twinkle = 0.65 + 0.35 * Math.sin(t * s.tw + s.ph);
      const a = s.a * twinkle;
      ctx.beginPath();
      ctx.fillStyle = `rgba(${Math.round(s.cr * 255)},${Math.round(s.cg * 255)},${Math.round(s.cb * 255)},${a.toFixed(3)})`;
      ctx.arc(s.x * W + driftX, s.y * H + driftY, s.r * dpr, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
